import { Fragment } from "react";

import dayjs from "dayjs";
import { renderToStaticMarkup } from "react-dom/server";

import { fullMonthAndYear, shortMonthAndYear } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue, ChartTooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { WEB_SOURCE_DISPLAY_NAME } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { PoPCompareChangeTooltip } from "pro-features/components/PoPCompareChangeTooltip/PoPCompareChangeTooltip";

import { POP_CURRENT_COLOR, POP_PREVIOUS_COLOR } from "./constants";
import { PopTooltipContainer, PopTooltipDivider, PopTooltipHeaderCell, PopTooltipValueCell } from "./PopTooltip";
import { tooltipColumnDefinition } from "./tooltipColumnDefinition";
import type { TooltipPoint, TooltipRow } from "./types";

const changeFormatter = percentageSignFilter();
const valueFormatter = minVisitsAbbrFilter();

export const formatValue = (value: number | null | undefined): string => String(valueFormatter(Number(value ?? 0)));

export interface PopTooltipConfig {
    tooltipChangeLookup: Record<string, number>;
    compareDateLookup: Record<number, string>;
    popPeriodLabels: { current: string; previous: string };
    webSource: string;
}

export function renderPopTooltipContent(timestamp: number, points: TooltipPoint[], config: PopTooltipConfig): string {
    const sitePairs: Record<string, { current: number | null; previous: number | null; change?: number }> = {};

    points.forEach((point) => {
        const seriesId: string = point.series?.userOptions?.id ?? point.series?.name ?? "";
        const isCurrent = seriesId.endsWith("-current");
        const site = seriesId.replace(/-(current|previous)$/, "");

        if (!sitePairs[site]) {
            sitePairs[site] = { current: null, previous: null };
        }

        if (isCurrent) {
            sitePairs[site].current = point.y;
            const lookupKey = `${seriesId}:${timestamp}`;
            if (config.tooltipChangeLookup[lookupKey] !== undefined) {
                sitePairs[site].change = config.tooltipChangeLookup[lookupKey];
            }
        } else {
            sitePairs[site].previous = point.y;
        }
    });

    const webSourceLabel =
        WEB_SOURCE_DISPLAY_NAME[config.webSource as keyof typeof WEB_SOURCE_DISPLAY_NAME] ??
        config.webSource ??
        "All traffic";

    const currentMonthLabel = dayjs.utc(timestamp).format(shortMonthAndYear);
    const compareDate = config.compareDateLookup[timestamp];
    const previousMonthLabel = compareDate
        ? dayjs.utc(compareDate).format(shortMonthAndYear)
        : config.popPeriodLabels.previous;

    return renderToStaticMarkup(
        <PopTooltipContainer>
            <Box />
            <PopTooltipHeaderCell color={POP_PREVIOUS_COLOR}>{previousMonthLabel}</PopTooltipHeaderCell>
            <PopTooltipHeaderCell color={POP_CURRENT_COLOR}>{currentMonthLabel}</PopTooltipHeaderCell>
            <PopTooltipHeaderCell>{translate("common.change")}</PopTooltipHeaderCell>
            <PopTooltipDivider />
            {Object.entries(sitePairs).map(([site, d]) => {
                const changeVal = d.change;

                return (
                    <Fragment key={site}>
                        <PopTooltipValueCell
                            color={colorsPalettes.carbon[200]}
                            justifyContent="flex-start"
                        >
                            {webSourceLabel}
                        </PopTooltipValueCell>
                        <PopTooltipValueCell>{formatValue(d.previous)}</PopTooltipValueCell>
                        <PopTooltipValueCell>{formatValue(d.current)}</PopTooltipValueCell>
                        <PopTooltipValueCell>
                            <ChangeValue
                                value={changeVal !== undefined ? String(changeFormatter(changeVal, 2)) : ""}
                                unsigned={changeVal === 0}
                                isDecrease={(changeVal ?? 0) < 0}
                            />
                        </PopTooltipValueCell>
                    </Fragment>
                );
            })}
        </PopTooltipContainer>,
    );
}

export interface ComparePopTooltipConfig {
    tooltipChangeLookup: Record<string, number>;
    compareValueLookup: Record<string, number | null>;
    compareDateLookup: Record<number, string>;
}

export function renderComparePopTooltipContent(
    timestamp: number,
    points: TooltipPoint[],
    config: ComparePopTooltipConfig,
): string {
    const currentMonthLabel = dayjs.utc(timestamp).format(shortMonthAndYear);
    const compareDate = config.compareDateLookup[timestamp];
    const previousMonthLabel = compareDate ? dayjs.utc(compareDate).format(shortMonthAndYear) : "";

    const pointsData = points.map((point) => {
        const seriesId: string = point.series?.userOptions?.id ?? point.series?.name ?? "";
        const lookupKey = `${seriesId}:${timestamp}`;
        const compareVal = config.compareValueLookup[lookupKey] ?? null;
        const changeVal = config.tooltipChangeLookup[lookupKey] ?? 0;

        return {
            key: seriesId,
            y: changeVal,
            series: {
                ...point.series,
                color: point.color,
            },
            point: {
                values: [
                    { ["Key"]: currentMonthLabel, ["Value"]: point.y ?? 0 },
                    { ["Key"]: previousMonthLabel, ["Value"]: compareVal ?? 0 },
                ],
            },
        };
    });

    return renderToStaticMarkup(
        <PoPCompareChangeTooltip
            headersList={[
                translate("common.domain"),
                previousMonthLabel,
                currentMonthLabel,
                translate("wa.traffic.engagement.pop.tooltip.growth"),
            ]}
            pointsData={pointsData}
            yFormatter={({ value }) => formatValue(Number(value))}
            changeFormatter={percentageSignFilter}
            cellWidth={100}
        />,
    );
}

export function renderSingleTooltipContent(
    timestamp: number,
    points: TooltipPoint[],
    tooltipChangeLookup: Record<string, number>,
): string {
    const title = dayjs.utc(timestamp).format(fullMonthAndYear);
    const rows: TooltipRow[] = points.map((point) => {
        const seriesId = point.series?.userOptions?.id ?? point.series?.name;
        const lookupKey = `${seriesId}:${timestamp}`;

        const changeValue = tooltipChangeLookup[lookupKey];

        return {
            domain: {
                label: point.series.name,
                color: point.color as StrictColor,
            },
            value: formatValue(point.y),
            change:
                changeValue === undefined
                    ? undefined
                    : {
                          value: String(changeFormatter(Math.abs(changeValue), 2)),
                          isDecrease: changeValue < 0,
                      },
        };
    });

    return renderToStaticMarkup(
        <ChartTooltip
            title={title}
            columnDefinition={tooltipColumnDefinition}
            data={rows}
        />,
    );
}
